import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@tackle-io/platform-ui';
import { useHistory } from 'react-router-dom';
import { useToast } from 'hooks';
import { AceInvitationResponse } from '../../types/AceInvitation.type';
import { generateRandomInvitation } from '../../utilities/randomInvitationGenerator';
import coSellClient from '../../../api/coSellClient';
import { TackleOperationId } from '../../../api/utils';
import { coSellLandingPageDataId } from '../../utilities/intercomEnums';

const TOAST_CONFIG = {
  options: {
    type: 'info',
    autoClose: false,
  },
};

const ERROR_TOAST_CONFIG = {
  options: {
    type: 'error',
    autoClose: 5000,
  },
};

interface RequestInvitationButtonProps {
  tackleOperationId: TackleOperationId;
  disabled?: boolean;
}

export const RequestInvitationButton: React.FC<
  RequestInvitationButtonProps
> = ({
  tackleOperationId = TackleOperationId.CREATE_INVITATION,
  disabled = false,
}) => {
  const [isLoading, setLoading] = useState(false);
  const { toaster } = useToast();
  const history = useHistory();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const safeSetLoading = (value: boolean) => {
    if (isMounted.current) {
      setLoading(value);
    }
  };

  const createRandomInvitation = async () => {
    safeSetLoading(true);

    try {
      const randomInvitation = generateRandomInvitation();
      const response = (await coSellClient.createMockInvitation(
        randomInvitation,
        tackleOperationId,
      )) as AceInvitationResponse;

      // Handle API response which may use either camelCase or PascalCase for the ID field
      const invitationId =
        response?.invitationId || (response as any)?.invitationID;
      if (!invitationId) {
        throw new Error('No invitation ID received');
      }

      // Show processing toast that persists while details page loads
      toaster({
        options: {
          ...TOAST_CONFIG.options,
          message: 'Processing invitation request...',
        },
      });

      // Reset loading state before navigation
      safeSetLoading(false);

      // Navigate to invitation details page
      history.push(`/co-sell/invitation/aws/${invitationId}`);
    } catch (error) {
      safeSetLoading(false);
      const errorMessage =
        error instanceof Error ? error.message : 'An unknown error occurred';
      toaster({
        options: {
          ...ERROR_TOAST_CONFIG.options,
          message: `Failed to create sandbox invitation: ${errorMessage}`,
        },
      });
    }
  };

  return (
    <Button
      variant="outlined"
      appearance="primary"
      id={coSellLandingPageDataId.INVITATION_CREATE_BUTTON}
      data-testid={coSellLandingPageDataId.INVITATION_CREATE_BUTTON}
      loading={isLoading}
      disabled={disabled || isLoading}
      onClick={createRandomInvitation}
    >
      Request invitation
    </Button>
  );
};
